import type { StampLibrary } from "../stamps/StampLibrary";
import type { StampMeta } from "../stamps/StampMeta";
import type { EditorContext } from "../tools/EditorContext";
import { importStampFromFile } from "../stamps/importStamp";
import { withUserStamp } from "../model/projectOps";

interface PanelState {
  category: StampMeta["category"] | "all";
}

/** Tools that pick a stamp from the library — only these show the panel. */
const STAMPING_TOOLS = new Set(["stamp", "lineStamp", "fill"]);

export class StampPanel {
  private root: HTMLElement;
  private ctx: EditorContext;
  private state: PanelState = { category: "all" };
  private onChange?: () => void;
  private toolId: string = "shape";

  constructor(root: HTMLElement, ctx: EditorContext, onChange?: () => void) {
    this.root = root;
    this.ctx = ctx;
    this.onChange = onChange;
    this.render();
  }

  setTool(toolId: string) {
    this.toolId = toolId;
    this.render();
  }

  refresh() {
    this.render();
  }

  private render() {
    if (!STAMPING_TOOLS.has(this.toolId)) {
      this.root.innerHTML = "";
      // Use the `hidden` attribute (set by default in the static
      // HTML, issue #181) so the rightbar reads as "Parts only"
      // until a stamping tool is actually active.
      this.root.setAttribute("hidden", "");
      return;
    }
    this.root.removeAttribute("hidden");
    const lib = this.ctx.library;
    const cats: PanelState["category"][] = [
      "all",
      "weave",
      "geometric",
      "border",
      "figure",
      "tool",
    ];
    this.root.innerHTML = "";
    const h = document.createElement("h3");
    h.textContent = "Stamps";
    this.root.appendChild(h);

    const catRow = document.createElement("div");
    catRow.className = "stamp-categories";
    for (const c of cats) {
      const tag = document.createElement("span");
      tag.className = "stamp-cat";
      tag.textContent = c;
      if (this.state.category === c) tag.classList.add("active");
      tag.addEventListener("click", () => {
        this.state.category = c;
        this.render();
      });
      catRow.appendChild(tag);
    }
    this.root.appendChild(catRow);

    const grid = document.createElement("div");
    grid.className = "stamp-grid";
    const stamps =
      this.state.category === "all"
        ? lib.all()
        : lib.byCategory(this.state.category);
    for (const stamp of stamps) {
      const tile = document.createElement("div");
      tile.className = "stamp-tile";
      tile.title = stamp.name;
      tile.innerHTML = stamp.svg;
      if (this.ctx.settings.activeStampId === stamp.id) {
        tile.classList.add("selected");
      }
      tile.addEventListener("click", () => {
        this.ctx.settings.activeStampId = stamp.id;
        this.render();
        this.onChange?.();
        this.ctx.notifySettingsChanged();
      });
      grid.appendChild(tile);
    }
    this.root.appendChild(grid);

    const upload = document.createElement("button");
    upload.className = "btn";
    upload.style.marginTop = "8px";
    upload.style.width = "100%";
    upload.textContent = "+ Upload SVG stamp";
    upload.addEventListener("click", async () => {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = ".svg,image/svg+xml";
      input.onchange = async () => {
        const f = input.files?.[0];
        if (!f) return;
        try {
          const meta = await importStampFromFile(f);
          this.ctx.library.add(meta);
          this.ctx.commit("Add stamp", (proj) =>
            withUserStamp(proj, {
              id: meta.id,
              name: meta.name,
              svg: meta.svg,
            }),
          );
          this.ctx.settings.activeStampId = meta.id;
          this.render();
          this.onChange?.();
          this.ctx.notifySettingsChanged();
        } catch (e) {
          alert("Failed to import stamp: " + (e as Error).message);
        }
      };
      input.click();
    });
    this.root.appendChild(upload);
  }
}
